import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  FetchStatus,
  LanderCookie,
  System,
  EventType,
  ErrorCode,
  QueryParam,
} from "../commons/Constants";
import {
  yahooXmlFetch,
  getYahooRSUri,
  extractPartnerTermsQs,
} from "./yahooFeed";
import { sendYahooEvent } from "./yahooEvent";
import { useLanderConfig } from "../commons/ConfigContext";
import { trackImpression, trackClick } from "../commons/signals";
import { getCookie } from "../commons/HelperFunctions";
import { YahooAds } from "./YahooAds";
import { YahooRS, RSItem } from "./YahooRS";
import { SearchBox } from "./SearchBox";
import { useYahooSingleLandersScript } from "./useYahooSingleLandersScript";
import { useRollerAdsBackfill } from "./useRollerAdsBackfill";
import { useOptimizedKeywordsExperiment } from "../experimentation/useOptimizedKeywordsExperiment";
import { RollerAdsKeywordLinks } from "../rollerads/RollerAdsKeywordLinks";
import { fetchRollerAds } from "../rollerads/rollerAdsFeed";
import { sendRollerAdsEvent } from "../rollerads/rollerAdsEvent";
import { isValidRedirectUrl } from "../rollerads/rollerAdsUtils";
import Logger from "../commons/Logger";
import classes from "./YahooAds.module.scss";
import PropTypes from "prop-types";
import classNames from "classnames";

interface YahooAdContentProps {
  isAdClicked: boolean;
  xmlResult: Document | null;
  keywords: string[];
  maxAds?: number;
  maxRS?: number;
  adIcon?: React.ReactNode;
  headerMessageId?: string;
}

const YahooAdContent: React.FC<YahooAdContentProps> = ({
  isAdClicked,
  xmlResult,
  keywords,
  maxAds,
  maxRS,
  adIcon,
  headerMessageId,
}) => {
  const partnerTermsQs = extractPartnerTermsQs(xmlResult);

  let relatedContent: React.ReactNode;
  if (isAdClicked) {
    relatedContent = (
      <YahooAds xmlResult={ xmlResult } maxAds={ maxAds } adIcon={ adIcon } />
    );
  } else {
    let rsItems: RSItem[];
    if (partnerTermsQs && keywords.length > 0) {
      rsItems = keywords.map((keyword) => ({
        term: keyword,
        qs: partnerTermsQs,
        searchQuery: keyword,
      }));
    } else {
      const resultSet =
        xmlResult
          ?.getElementById("relatedTerms")
          ?.getElementsByTagName("Search") ?? [];
      rsItems = Array.from(resultSet).map((el) => ({
        term: el.innerHTML,
        qs: el.getAttribute("queryParams") || "",
        searchQuery: "",
      }));
    }
    relatedContent = (
      <YahooRS
        items={ rsItems }
        maxRS={ maxRS }
        headerMessageId={ headerMessageId }
      />
    );
  }

  return (
    <>
      {relatedContent}
      <SearchBox xmlResult={ xmlResult } />
    </>
  );
};

YahooAdContent.propTypes = {
  isAdClicked: PropTypes.bool,
  xmlResult: PropTypes.instanceOf(Document),
};

interface YahooContentProps {
  maxAds?: number;
  maxRS?: number;
  adIcon?: React.ReactNode;
  headerMessageId?: string;
}

function getAdCount(xmlResult: Document | null): number {
  return (
    xmlResult?.getElementById("adListings")?.getElementsByTagName("Listing")
      ?.length ?? 0
  );
}

async function handleKeywordClick(
  keyword: string,
  landerConfig: any,
  setBackfillFetchState: (s: number) => void
) {
  setBackfillFetchState(FetchStatus.PENDING);

  sendRollerAdsEvent(EventType.KEYWORD_CLICK, landerConfig, {
    keyword,
    isBackfill: true,
  });

  try {
    const result = await fetchRollerAds({
      rootDomain: landerConfig.domain.rootDomain,
      customerId: landerConfig.customerId,
      keywords: [keyword],
      keywordClick: true,
    });

    if (result.status === 200 && result.ads.length > 0) {
      const firstAd = result.ads[0];

      if (!isValidRedirectUrl(firstAd.url)) {
        setBackfillFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          errorCode: ErrorCode.ERROR_ROLLERADS_INVALID_URL,
          isBackfill: true,
        });
        Logger.error("RollerAds backfill invalid redirect URL:", firstAd.url);
        return;
      }

      trackClick({ section: "ads", widget: "ad_redirect" });
      sendRollerAdsEvent(EventType.AD_REDIRECT, landerConfig, {
        bid: firstAd.bid,
        campaignId: firstAd.campaign_id,
        adUrl: firstAd.url,
        keyword,
        isBackfill: true,
      });

      window.location.href = firstAd.url;
    } else {
      setBackfillFetchState(FetchStatus.FAILURE);
      sendRollerAdsEvent(EventType.ERROR, landerConfig, {
        errorCode:
          result.status === 0
            ? ErrorCode.ERROR_ROLLERADS_BLOCKED
            : ErrorCode.ERROR_ROLLERADS_NO_ADS,
        isBackfill: true,
      });
    }
  } catch (error) {
    setBackfillFetchState(FetchStatus.FAILURE);
    sendRollerAdsEvent(EventType.ERROR, landerConfig, {
      errorCode: ErrorCode.ERROR_ROLLERADS_BLOCKED,
      isBackfill: true,
    });
    Logger.error("RollerAds backfill fetch failed:", error);
  }
}

interface YahooBackfillProps {
  keywords: string[];
  landerConfig: any;
}

const YahooBackfill: React.FC<YahooBackfillProps> = ({
  keywords,
  landerConfig,
}) => {
  const [backfillFetchState, setBackfillFetchState] = useState<number>(
    FetchStatus.PENDING
  );
  const isClickInFlight = useRef(false);
  const hasBackfillEventFired = useRef(false);

  useEffect(() => {
    if (hasBackfillEventFired.current) return;
    hasBackfillEventFired.current = true;
    sendRollerAdsEvent(EventType.ROLLERADS_BACKFILL_TRIGGER, landerConfig, {
      isBackfill: true,
    });
  }, [landerConfig]);

  const handleBackfillKeywordClick = useCallback(
    (keyword: string) => {
      if (isClickInFlight.current) return;
      isClickInFlight.current = true;
      handleKeywordClick(keyword, landerConfig, setBackfillFetchState).finally(
        () => {
          isClickInFlight.current = false;
        }
      );
    },
    [landerConfig]
  );

  return (
    <RollerAdsKeywordLinks
      keywords={ keywords }
      fetchState={ backfillFetchState }
      onKeywordClick={ handleBackfillKeywordClick }
    />
  );
};

function useYahooFetch(landerConfig: any, isAdClicked: boolean) {
  const [fetchState, setFetchState] = useState<number>(FetchStatus.PENDING);
  const [xmlResult, setXmlResult] = useState<Document | null>(null);

  useEffect(() => {
    if (!getCookie(LanderCookie.CLIENT_IP)) {
      setFetchState(FetchStatus.BLOCKED);
      setXmlResult(null);
      return;
    }
    let ignore = false;

    let yahooUrl: string;
    if (isAdClicked) yahooUrl = window.location.search;
    else
      yahooUrl = getYahooRSUri({
        rootDomain: landerConfig.domain.rootDomain,
        system: landerConfig.system,
        customerId: landerConfig.customerId,
        typeTag: landerConfig.yahoo?.typeTag,
      });

    yahooXmlFetch(yahooUrl).then((v) => {
      if (ignore) return;
      setFetchState(v.fetchStatus);
      setXmlResult(v.xmlResult);

      if (v.fetchStatus === FetchStatus.FAILURE) {
        sendYahooEvent(EventType.ERROR, landerConfig, {
          yahooApiMessage: v.message,
          errorCode: isAdClicked
            ? ErrorCode.ERROR_YAHOO_FAILED_FETCH_ADS
            : ErrorCode.ERROR_YAHOO_FAILED_FETCH_RS,
        });
      }
    });

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    landerConfig.domain.rootDomain,
    landerConfig.system,
    landerConfig.customerId,
    landerConfig.yahoo?.typeTag,
    isAdClicked,
  ]);

  return { fetchState, xmlResult };
}

interface YahooContentBodyProps {
  fetchState: number;
  shouldBackfill: boolean;
  backfillKeywords: string[];
  keywords: string[];
  landerConfig: any;
  isAdClicked: boolean;
  xmlResult: Document | null;
  maxAds?: number;
  maxRS?: number;
  adIcon?: React.ReactNode;
  headerMessageId?: string;
}

const YahooContentBody: React.FC<YahooContentBodyProps> = ({
  fetchState,
  shouldBackfill,
  backfillKeywords,
  keywords,
  landerConfig,
  isAdClicked,
  xmlResult,
  maxAds,
  maxRS,
  adIcon,
  headerMessageId,
}) => {
  if (fetchState === FetchStatus.PENDING) {
    return null;
  }
  if (shouldBackfill) {
    return (
      <YahooBackfill keywords={ backfillKeywords } landerConfig={ landerConfig } />
    );
  }
  if (fetchState === FetchStatus.SUCCESS) {
    return (
      <YahooAdContent
        isAdClicked={ isAdClicked }
        xmlResult={ xmlResult }
        keywords={ keywords }
        maxAds={ maxAds }
        maxRS={ maxRS }
        adIcon={ adIcon }
        headerMessageId={ headerMessageId }
      />
    );
  }
  if (
    fetchState === FetchStatus.FAILURE ||
    fetchState === FetchStatus.BLOCKED
  ) {
    return (
      <div className={ classes.blockedContent }>
        <FormattedMessage id="contentBlockedText" />
      </div>
    );
  }
  return null;
};

// Resolve the keywords supplied to Yahoo Partner Supplied Terms (PARKING-5154).
// Treatment's optimizedKeywords always win. For non-experiment traffic, only
// customer-set keywords (DB/CSV) or ?term= overrides become Partner Supplied
// Terms; tokenizer keywords fall through to Yahoo's own relatedTerms.
function resolvePartnerTerms({
  mergedKeywords,
  inTreatment,
  optimizedKeywords,
  keywordSource,
  hasTermOverride,
}: {
  mergedKeywords: string[] | undefined;
  inTreatment: boolean;
  optimizedKeywords: string[] | undefined;
  keywordSource: string | undefined;
  hasTermOverride: boolean;
}): string[] {
  if (inTreatment) return optimizedKeywords ?? [];
  if (!mergedKeywords?.length) return [];
  if (hasTermOverride) return mergedKeywords;
  if (keywordSource === "DB" || keywordSource === "CSV") return mergedKeywords;
  return [];
}

export const YahooContent: React.FC<YahooContentProps> = ({
  maxAds,
  maxRS,
  adIcon,
  headerMessageId,
}) => {
  const { landerConfig, mergedConfig, queryConfig } = useLanderConfig();
  const { isBackfillEligible } = useRollerAdsBackfill();
  const { inOptimizedKeywordsTreatment } = useOptimizedKeywordsExperiment();

  const isAdClicked = new URLSearchParams(window.location.search).has("qs");
  const { fetchState, xmlResult } = useYahooFetch(landerConfig, isAdClicked);

  useYahooSingleLandersScript(xmlResult);

  useEffect(() => {
    sendYahooEvent(EventType.VISIT, landerConfig);
    trackImpression({ section: "page", widget: "lander_page" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backfillKeywords = mergedConfig.keywords?.length
    ? mergedConfig.keywords
    : landerConfig.adSense?.keywords ?? [];

  const shouldBackfill =
    isAdClicked &&
    isBackfillEligible &&
    backfillKeywords.length > 0 &&
    fetchState !== FetchStatus.PENDING &&
    fetchState !== FetchStatus.BLOCKED &&
    (fetchState === FetchStatus.FAILURE || getAdCount(xmlResult) === 0);

  const hasTermOverride = !!queryConfig[QueryParam.TERM];
  const partnerTerms = resolvePartnerTerms({
    mergedKeywords: mergedConfig.keywords,
    inTreatment: inOptimizedKeywordsTreatment,
    optimizedKeywords: landerConfig.optimizedKeywords,
    keywordSource: landerConfig.adSense?.keywordSource,
    hasTermOverride,
  });

  return (
    <div
      className={ classNames(classes.yahooFeed, {
        [classes.pwYahoo]: landerConfig.system === System.PARKWEB,
      }) }
    >
      <YahooContentBody
        fetchState={ fetchState }
        shouldBackfill={ shouldBackfill }
        backfillKeywords={ backfillKeywords }
        keywords={ partnerTerms }
        landerConfig={ landerConfig }
        isAdClicked={ isAdClicked }
        xmlResult={ xmlResult }
        maxAds={ maxAds }
        maxRS={ maxRS }
        adIcon={ adIcon }
        headerMessageId={ headerMessageId }
      />
    </div>
  );
};
